import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Loader2, AlertCircle, Check, Home } from 'lucide-react';
import { acceptInvite, previewInvite, InvitePreview } from '../api/client';
import { useAuth } from '../context/AuthContext';

const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.solaniq.mobile';
// A store search link until the App Store listing URL is known.
const APP_STORE_URL = 'https://apps.apple.com/au/search?term=SolanIQ';

const isMobileDevice = () =>
  typeof navigator !== 'undefined' && /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

/**
 * /invite/:token — the link in an account member invite e-mail. On a phone
 * it's app-first: it tries once to open the SolanIQ app at
 * solaniq://invite/<token> (e-mail apps strip custom-scheme links, so the
 * e-mail itself links here), with store links and the web flow as fallbacks.
 * The web flow works with any sign-in (the account's e-mail needn't match the
 * invited address): signed out, sign in and come back here; signed in,
 * accept with one tap.
 */
export function AcceptInvite() {
  const { token = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, refreshUser } = useAuth();

  const [invite, setInvite] = useState<InvitePreview | null>(null);
  const [state, setState] = useState<'loading' | 'ready' | 'invalid' | 'accepting' | 'done'>(
    'loading'
  );
  const [error, setError] = useState<string | null>(null);
  const isMobile = useMemo(isMobileDevice, []);
  const appLink = `solaniq://invite/${encodeURIComponent(token)}`;
  // On a phone the web flow is tucked behind "Or accept here on the web",
  // unless they're already signed in here (e.g. back from signing in).
  const [showWeb, setShowWeb] = useState(false);

  // Phones: try the app once per invite per browser session (no loop when
  // they come back from signing in on the web).
  useEffect(() => {
    if (!isMobile || state !== 'ready' || !invite) return;
    const key = `solaniq.inviteAppTried.${token}`;
    try {
      if (sessionStorage.getItem(key)) return;
      sessionStorage.setItem(key, '1');
    } catch {
      // Storage blocked: still only reached once per mount.
    }
    window.location.href = appLink;
  }, [isMobile, state, invite, token, appLink]);

  useEffect(() => {
    let cancelled = false;
    previewInvite(token)
      .then((res) => {
        if (cancelled) return;
        if (res.valid) {
          setInvite(res);
          setState('ready');
        } else {
          setState('invalid');
        }
      })
      .catch(() => {
        if (!cancelled) setState('invalid');
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const onAccept = async () => {
    setError(null);
    setState('accepting');
    try {
      await acceptInvite({ token });
      setState('done');
    } catch (err) {
      const status = (err as { response?: { status?: number } })?.response?.status;
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail;
      if (status === 409) {
        // Already a member of this home: nothing more to do.
        setState('done');
        return;
      }
      setError(detail || 'Could not accept the invite. Please try again.');
      setState('ready');
    }
  };

  const goToDashboard = async () => {
    await refreshUser();
    navigate('/', { replace: true });
  };

  const card =
    'card p-6 text-center';
  const primary =
    'inline-flex items-center justify-center w-full px-4 py-2.5 text-sm font-medium text-white bg-brand-orange hover:bg-brand-orange-dark rounded-full disabled:opacity-60';
  const secondary =
    'inline-flex items-center justify-center w-full px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-800 rounded-lg';

  if (state === 'loading' || isLoading) {
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="max-w-md mx-auto px-4 py-12">
      <div className={card}>
        {state === 'invalid' && (
          <>
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 mb-3">
              <AlertCircle className="w-6 h-6 text-red-700 dark:text-red-300" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Invite not available</h1>
            <p className="text-sm text-gray-700 dark:text-gray-300">
              This invite link is invalid or has expired. Ask the account holder to send a new one.
            </p>
            <Link to="/" className="inline-block mt-4 text-sm font-medium text-brand-orange">
              Go to SolanIQ
            </Link>
          </>
        )}

        {(state === 'ready' || state === 'accepting') && invite && (
          <>
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-orange-100 dark:bg-brand-orange/10 mb-3">
              <Home className="w-6 h-6 text-brand-orange" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">You&rsquo;re invited</h1>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-5">
              <strong>{invite.owner_email_masked}</strong> invited you to view their home on SolanIQ: the
              battery, solar and prices, and EV charging.
            </p>
            {isMobile && !isAuthenticated && !showWeb ? (
              <>
                <a href={appLink} className={primary}>
                  Open in the SolanIQ app
                </a>
                <p className="text-sm text-gray-700 dark:text-gray-300 mt-5 mb-2">Don&rsquo;t have the app?</p>
                <div className="grid grid-cols-2 gap-2">
                  <a href={PLAY_STORE_URL} className={secondary} target="_blank" rel="noreferrer">
                    Google Play
                  </a>
                  <a href={APP_STORE_URL} className={secondary} target="_blank" rel="noreferrer">
                    App Store
                  </a>
                </div>
                <button
                  type="button"
                  onClick={() => setShowWeb(true)}
                  className="mt-4 text-sm font-medium text-brand-orange"
                >
                  Or accept here on the web
                </button>
              </>
            ) : (
              <>
                {error && <p className="text-sm text-red-600 dark:text-red-400 mb-3">{error}</p>}
                {isAuthenticated ? (
                  <button type="button" className={primary} onClick={onAccept} disabled={state === 'accepting'}>
                    {state === 'accepting' ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Accept invite'}
                  </button>
                ) : (
                  <>
                    <Link to="/login" state={{ from: location }} className={primary}>
                      Sign in to accept
                    </Link>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
                      No account? Sign in with Google or Apple and one is created.
                    </p>
                  </>
                )}
                {!isMobile && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-4">
                    On your phone? Open this link there to accept in the SolanIQ app.
                  </p>
                )}
              </>
            )}
          </>
        )}

        {state === 'done' && (
          <>
            <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 mb-3">
              <Check className="w-6 h-6 text-green-700 dark:text-green-300" />
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">You now have access</h1>
            <p className="text-sm text-gray-700 dark:text-gray-300 mb-5">
              Open the SolanIQ app, or use the dashboard here.
            </p>
            <div className="space-y-2">
              <a href="solaniq://invite/accepted" className={primary}>
                Open the SolanIQ app
              </a>
              <button type="button" className={secondary} onClick={goToDashboard}>
                Go to dashboard
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
